import { useEffect, useState } from "react";
import { useNavigate, useLocation, useParams, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { LockOutlined } from "@ant-design/icons";

import { Post, getPostsBySubcategory } from "../api/postAPI";
import { SubColor } from "../style/styleValue";

const PostList = () => {
  const [postList, setPostList] = useState<Post[]>([]);

  const navigate = useNavigate();
  const location = useLocation();
  const { categoryId } = useParams();
  const [searchParams] = useSearchParams();

  const fetchAllPosts = async () => {
    if (!categoryId) return;

    try {
      const res = await getPostsBySubcategory(1, 1000, +categoryId, 'modifiedAt', 'DESC', null, null);
      setPostList(res);
    } catch (error) {
      console.error('Error in fetchAllPosts:', error);
      setPostList([]);
    }
  };

  useEffect(() => {
    fetchAllPosts();
  }, [categoryId]);

  // 쿼리파라미터에서 organ name 추출
  const organName = searchParams.get('name') || "게시판";

  return (
    <Container>
      <HeaderTitle>{organName}</HeaderTitle>
      <CardGrid>
        {postList.map((cardData, idx) => (
          <PostCard
            key={`${cardData?.title}_${idx}`}
            onClick={() => {
              const params = new URLSearchParams();
              params.set('title', cardData.title); // 현재 post 제목
              params.set('from', 'gallery'); // 오리진 표시
              if (organName !== "게시판") {
                params.set('name', organName); // 부모 organ 이름
              }
              if (categoryId) {
                params.set('categoryId', categoryId); // gallery organ categoryId
              }
              navigate(`/post/${cardData?.id}?${params.toString()}`);
            }}
          >
            {cardData.isSecret && (
              <SecretOverlay>
                <LockOutlined />
              </SecretOverlay>
            )}
            <ImageWrapper>
              {cardData.postThumbnail ? (
                <img
                  alt={cardData.title}
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/photo/${cardData.postThumbnail}`}
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                    (e.target as HTMLImageElement).parentElement!.style.backgroundColor = '#f0f0f0';
                  }}
                />
              ) : (
                <div style={{ width: '100%', height: '100%', backgroundColor: "#e6e6e6" }} />
              )}
            </ImageWrapper>
            <CardContent>
              <CardTitle>{cardData.title}</CardTitle>
              <CardWriter>{cardData.writer}</CardWriter>
            </CardContent>
          </PostCard>
        ))}
      </CardGrid>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  max-width: 1800px;
  margin: 2rem auto;
  padding: 0 8rem;
`;

const HeaderTitle = styled.h1`
  text-align: center;
  margin-bottom: 2rem;
  color: ${SubColor};
  font-weight: 400;
  font-size: 2.5rem;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 24px;
  padding: 1rem 0;
  align-items: stretch; 
`;

const PostCard = styled.div`
  position: relative;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%; 

  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
`;

const ImageWrapper = styled.div`
  width: 100%;
  height: 180px;
  background-color: #f0f0f0;
  flex-shrink: 0;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s;
  }

  ${PostCard}:hover & img {
    transform: scale(1.05);
  }
`;

const CardContent = styled.div`
  padding: 16px;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const CardTitle = styled.h3`
  display: flex;
  margin: 0 0 12px 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  line-height: 1.5;
  white-space: normal; 
  word-wrap: break-word;
  word-break: keep-all;
  align-items: flex-start;
  gap: 6px;
`;

const CardWriter = styled.div`
  font-size: 0.9rem;
  color: #888;
  margin-top: auto;
`;

const SecretOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  
  background-color: rgba(245, 245, 245, 0.7); 
  
  backdrop-filter: blur(0.5px); 
  
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
  
  font-size: 3rem; 
  color: #8c8c8c;
`;

export default PostList;